// profileFormat.js — v238.24.0
// Pure profile formatters for the records-export face sheet. Ported from
// MyMedicalNote so the face sheet matches what patients see on their note.

import { parseDate } from './dateUtils'
// TEST 1.03 — the ONE coercion; see src/utils/profileConditions.js.
import { toConditionList } from './profileConditions'

export function getPatientName(profile, user) {
  const first = profile?.identification?.firstName || profile?.firstName || user?.firstName || ''
  const last = profile?.identification?.lastName || profile?.lastName || user?.lastName || ''
  const full = `${first} ${last}`.trim()
  return full || '[Not documented]'
}

// ★★★ v242.146.0 — BOTH OF THESE BUILT THEIR OWN CALENDAR, and this module
//     feeds the records-export face sheet, so a patient's own exported records
//     printed their date of birth ONE DAY EARLY. `new Date('1984-02-11')` is
//     UTC midnight; displayed west of UTC that is the 10th. `parseDate` builds
//     a date-only value at LOCAL NOON, which is what it exists for.
export function formatDob(dob) {
  if (!dob) return '[Not documented]'
  const d = parseDate(dob)
  if (!d) return String(dob)
  try {
    return d.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
  } catch {
    return String(dob)
  }
}

export function calculateAge(dob) {
  if (!dob) return '?'
  const d = parseDate(dob)
  if (!d) return '?'
  const now = new Date()
  let age = now.getFullYear() - d.getFullYear()
  const m = now.getMonth() - d.getMonth()
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--
  return age >= 0 ? String(age) : '?'
}

// ★ PROD-407 (U1-7c F5; Alex Q57 = yes): the twin of orella-api's _cr_fmt_allergies (the case
//   report PDF). ★ U2 push 2 review r1 M1: a LINE-FOR-LINE port of _cr_fmt_allergies as it is at
//   orella-api e40fd85 (B9), so both print the same text for the same section. The first port
//   (U1-7c's earlier fix shape) differed from it on 7 of 11 measured inputs: it printed a negative
//   beside a generic `list` entry ("Food: NKFA" beside a reported Sulfa), read a legacy
//   foodAllergies string as "not specified", lost a records-import `substance`, printed a blank
//   entry instead of the negative, and repeated an Other contact on the "Reported:" line.
//   The rules, as the API's docstring states them:
//   * a LISTED allergy always wins over a false flag beside it (PROD-360): foodList / medList (or
//     the legacy foodAllergies / drugAllergies) is printed whenever it is non-empty, and a
//     negative only when its flag is false AND nothing is listed;
//   * contactList is printed (bare ids by their label, 'other' as "Other: <text>");
//   * the generic `list` entries no section already names go on a "Reported:" line beside the
//     sections, and while one does, no negative is printed (its kind is unknown: R9); with no
//     section, the list alone, before the hasAllergies=false negative (AF1).
//   Python semantics kept where they decide the text: truthiness (py), str() (pyStr), casefold
//   (fold), `is False` / `is True` (=== false / === true), exact dict lookups (no trimming or
//   lowercasing of a contact id). scripts/check-allergy-listed-never-none.mjs PARITY holds this
//   function to the API's own output over scripts/lib/allergy-parity-fixtures.json (written by
//   scripts/gen-allergy-parity.py from the API source). If the API's function changes, re-run
//   the generator and follow it here.
const SIMPLE_CONTACT_NAMES = Object.freeze({ latex: 'Latex', adhesive: 'Adhesive/Medical Tape', chlorhexidine: 'Chlorhexidine',
  fragrances: 'Fragrances', nickel: 'Nickel', other: 'Other' })
const isDict = (x) => x !== null && typeof x === 'object' && !Array.isArray(x)
// Python truthiness of a JSON value.
const py = (x) => !(x === undefined || x === null || x === false || x === 0 || x === '' || (Array.isArray(x) && x.length === 0) || (isDict(x) && Object.keys(x).length === 0))
// Python str() of a JSON scalar (a list or dict entry is not a shape the profile stores).
const pyStr = (x) => (x === true ? 'True' : x === false ? 'False' : x === null || x === undefined ? 'None' : String(x))
// str.casefold(), as near as JS gets: the upper-then-lower round trip folds ß to ss.
const fold = (s) => String(s).toUpperCase().toLowerCase()

export function formatAllergies(profile) {
  const a = (profile || {}).allergies
  if (!py(a) || !isDict(a)) return '[Not documented]'

  const nameOf = (x) => {
    if (isDict(x)) {
      for (const k of ['name', 'allergen', 'substance']) {
        if (typeof x[k] === 'string' && x[k].trim()) return x[k].trim()
      }
      return ''
    }
    return typeof x === 'string' ? x.trim() : pyStr(x)
  }
  const one = (x) => {
    if (!isDict(x)) return pyStr(x)
    let e = nameOf(x)
    if (py(x.severity)) e += ` (${pyStr(x.severity)})`
    if (py(x.reaction)) e += ` - ${pyStr(x.reaction)}`
    return e
  }
  const listed = (...keys) => {
    for (const k of keys) {
      const v = a[k]
      let items = typeof v === 'string' ? [v] : Array.isArray(v) ? [...v] : []
      items = items.filter((x) => x !== null && x !== undefined && !(typeof x === 'string' && !x.trim()))
      if (items.length) return items
    }
    return []
  }

  const food = listed('foodList', 'foodAllergies')
  const meds = listed('medList', 'drugAllergies')
  const contacts = listed('contactList')
  const shown = new Set([...food, ...meds].filter((x) => nameOf(x)).map((x) => fold(nameOf(x))))
  const other = typeof a.otherContactAllergy === 'string' ? a.otherContactAllergy.trim() : ''
  const contactLabels = []
  for (const x of contacts) {
    if (isDict(x)) {
      contactLabels.push(one(x))
      if (nameOf(x)) shown.add(fold(nameOf(x)))
    } else if (x === 'other') {
      contactLabels.push(other ? `Other: ${other}` : 'Other')
      if (other) shown.add(fold(other))
    } else {
      const label = typeof x === 'string'
        ? (Object.prototype.hasOwnProperty.call(SIMPLE_CONTACT_NAMES, x) ? SIMPLE_CONTACT_NAMES[x] : x)
        : pyStr(x)
      contactLabels.push(label)
      shown.add(fold(label))
      shown.add(fold(pyStr(x)))
    }
  }
  const generic = listed('list')
  const extra = generic.filter((x) => !(nameOf(x) && shown.has(fold(nameOf(x)))))

  const parts = []
  if (food.length) parts.push('Food: ' + food.map(one).join(', '))
  else if (a.hasFoodAllergies === false && !extra.length) parts.push('Food: NKFA (No Known Food Allergies)')
  else if (a.hasFoodAllergies === true) parts.push('Food: Has food allergies (not specified)')
  if (meds.length) parts.push('Medication: ' + meds.map(one).join(', '))
  else if (a.hasMedAllergies === false && !extra.length) parts.push('Medication: NKDA (No Known Drug Allergies)')
  else if (a.hasMedAllergies === true) parts.push('Medication: Has drug allergies (not specified)')
  if (contactLabels.length) parts.push('Contact: ' + contactLabels.join(', '))
  if (parts.length) {
    if (extra.length) parts.push('Reported: ' + extra.map(one).join(', '))
    return parts.join('\n')
  }
  // ★ PROD-398 AF1: what is LISTED wins over a false flag (PROD-360's rule), so the list is read
  //   before the negative.
  if (generic.length) return generic.map(one).join('\n')
  if (a.hasAllergies === false) return 'NKDA (No Known Drug Allergies)'
  return '[Not documented]'
}

export function formatMedications(profile) {
  if (!profile?.medications) return '[Not documented]'
  if (profile.medications.hasMedications === false) return 'None'
  if (profile.medications.list?.length > 0) {
    return profile.medications.list.map((m) => {
      let entry = m.name
      if (m.dosage) entry += ` ${m.dosage}`
      if (m.frequency) entry += `, ${m.frequency}`
      if (m.purpose) entry += ` (for ${m.purpose})`
      return entry
    }).join('\n')
  }
  return '[Not documented]'
}

function safeName(item) {
  if (!item) return ''
  if (typeof item === 'string') return item
  if (typeof item === 'object') {
    const name = item.name || item.condition || item.label || item.title || ''
    if (typeof name === 'object') return JSON.stringify(name)
    return name ? String(name) : JSON.stringify(item)
  }
  return String(item)
}
function safeYear(item) {
  if (!item || typeof item !== 'object') return null
  return item.year || item.date || null
}

export function formatConditions(profile) {
  const list = toConditionList(profile?.medicalHistory?.conditions)
  if (!list.length) return 'None reported'
  const out = list.map((c) => {
    const name = safeName(c); const year = safeYear(c)
    return year ? `${name} (${year})` : name
  }).filter(Boolean).join(', ')
  return out || 'None reported'
}

export function formatSurgeries(profile) {
  const list = profile?.medicalHistory?.surgeries
  if (!list?.length) return 'None reported'
  const seen = new Set()
  const out = list.map((s) => {
    const name = safeName(s); const year = safeYear(s)
    const key = `${name}|${year || ''}`
    if (seen.has(key)) return null
    seen.add(key)
    return year ? `${name} (${year})` : name
  }).filter(Boolean).join(', ')
  return out || 'None reported'
}

export function formatInsurance(profile) {
  const ins = profile?.insurance?.primary || profile?.insurance || {}
  const carrier = ins.carrier || ins.payerName || ins.payer || ins.company || ''
  const memberId = ins.memberId || ins.memberID || ins.subscriberId || ''
  const group = ins.groupNumber || ins.group || ''
  const out = []
  if (carrier) out.push(`Carrier: ${carrier}`)
  if (memberId) out.push(`Member ID: ${memberId}`)
  if (group) out.push(`Group: ${group}`)
  return out.length ? out.join('\n') : '[Not documented]'
}
